import { addressHasData } from './AddressFields';

/**
 * Beneficiary percentage arithmetic.
 *
 * Everything works in integer hundredths ("cents") rather than floats.
 * `33.33 + 33.33 + 33.34` evaluates to 100.00000000000001 in IEEE-754, so a
 * float sum compared against 100 would reject a perfectly valid split. Two
 * decimal places rounded to an integer is exact.
 */

/** Parse a percentage string into integer hundredths (100.00% -> 10000). */
export function toCents(v) {
  const n = parseFloat(String(v ?? '').replace('%', '').trim());
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

/** Format integer hundredths back for display (10000 -> "100.00"). */
export function formatCents(cents) {
  return (Number(cents || 0) / 100).toFixed(2);
}

/** True when the client has typed anything at all into a beneficiary row. */
export function rowHasData(r) {
  if (!r) return false;
  const scalar = ['pct', 'relationship', 'full_name', 'dob']
    .some((k) => String(r[k] ?? '').trim());
  return scalar || addressHasData(r.address);
}

export function filledRows(rows) {
  return (rows || []).filter(rowHasData);
}

export function sumCents(rows) {
  return filledRows(rows)
    .reduce((acc, r) => acc + (toCents(r.pct) ?? 0), 0);
}

/**
 * Validate that a beneficiary group totals exactly 100.00%.
 *
 * Returns null when the group is valid *or* entirely unused — contingent
 * beneficiaries are optional, so an empty group must not block the client.
 *
 * @param {Array} rows
 * @param {string} label - "Primary" or "Contingent", used in the message.
 */
export function percentGroupError(rows, label) {
  const filled = filledRows(rows);
  if (filled.length === 0) return null;

  if (filled.some((r) => toCents(r.pct) === null)) {
    return `Every ${label.toLowerCase()} beneficiary needs a percentage.`;
  }

  const total = sumCents(filled);
  if (total === 10000) return null;

  const shown = formatCents(total);
  const hint = total < 10000
    ? ` Add ${formatCents(10000 - total)}%.`
    : ` Remove ${formatCents(total - 10000)}%.`;
  return (
    `${label} percentages must total exactly 100.00% (currently ${shown}%).`
    + hint
    + ' Tip: 33.33 + 33.33 + 33.33 = 99.99 — change one of them to 33.34.'
  );
}
